/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SECRETS from './secrets';
import AppBar from './Components/AppBar';
import COLORS from './Components/styles/Colors';

const pageStyle = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const StyledTable = styled(Table)(() => ({
  borderRadius: 0,
  '& .MuiTableCell-head': {
    color: COLORS.SLATE50,
    backgroundColor: 'rgb(64, 64, 64)',
    borderRadius: 0,
    padding: '0.25rem 0.875rem',
  },
  '& .MuiTableCell-body': { padding: '0.25rem 0.875rem' },
}));

const footerStyle = {
  backgroundColor: 'rgb(64, 64, 64)',
  color: 'rgba(241, 245, 249, 0.9)',
  textAlign: 'right',
  padding: '5px',
  paddingRight: '25px',
  fontFamily: 'Roboto',
  fontSize: '0.875rem',
  minHeight: '38px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  fontWeight: 'bold',
  flexShrink: 0,
};

const ROW_HEIGHT = 33;
const MIN_ROWS = 15;

const twoDigits = (num) => ((num <= 9) ? `0${num}` : `${num}`);

const getFechaHoy = () => {
  const date = new Date();
  return `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return '—';
  const fecha = new Date(fechaStr);
  return fecha.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Mexico_City',
  });
};

const formatHora = (fechaStr) => {
  if (!fechaStr) return '—';
  const fecha = new Date(fechaStr);
  return fecha.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Mexico_City',
  });
};

const CheckerLoginsScreen = ({
  isLogged,
  token,
  logOut,
  secondary,
}) => {
  const [logins, setLogins] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [dateFilter, setDateFilter] = useState(getFechaHoy);

  const fetchLogins = async (fecha) => {
    try {
      axios.defaults.headers.common.Authorization = `bearer ${token}`;
      axios.defaults.baseURL = SECRETS.SERVERURL;
      const response = await axios.get(`/logloginorg/checadores?fecha=${fecha}`);
      setLogins(response.data);
    } catch (e) {
      console.error('error al obtener logins de checadores:', e);
    }
  };

  useEffect(() => {
    fetchLogins(dateFilter);
  }, []);

  const handleDateFilter = (nuevaFecha) => {
    setDateFilter(nuevaFecha);
    fetchLogins(nuevaFecha);
  };

  const liftingSearch = (searchValueString) => setSearchValue(searchValueString);

  const filteredLogins = logins.filter((row) => {
    const removeTilde = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const stringToSearch = searchValue.toLowerCase() || '';
    if (stringToSearch === '') return true;
    const regex = new RegExp(`.*${removeTilde(stringToSearch)}.*`);
    return (
      regex.test(removeTilde((row.nombre ?? '').toLowerCase()))
      || regex.test(removeTilde((row.telefono ?? '').toLowerCase()))
    );
  });

  const ghostRows = Math.max(0, MIN_ROWS - filteredLogins.length);

  if (isLogged !== 'true') return <Navigate to="/" replace />;

  return (
    <div style={pageStyle}>
      <AppBar
        visibleStateRows={[]}
        dateFilter={dateFilter}
        handleDateFilter={handleDateFilter}
        dateFrom={undefined}
        dateTo={undefined}
        handleDateFrom={undefined}
        handleDateTo={undefined}
        filterRoutes={[]}
        rutas={[]}
        newTracksList={undefined}
        searchBarTextDisplay={searchValue}
        secondary={secondary}
        handleSearch={liftingSearch}
        handleLogOut={logOut}
        handleStateRow={false}
        handleFilterRoutes={() => { }}
        handleUpdateTracks={undefined}
        recoversCountMessage={undefined}
        isFilterStringToday={undefined}
        location="logincheckers"
        handleCopy={undefined}
        isVisibleNewTracksLength={false}
        handleUptTracks={undefined}
      />
      <Paper sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      >
        <TableContainer
          className="last-checkpoint-table"
          sx={{ flex: 1, overflow: 'auto' }}
        >
          <StyledTable stickyHeader aria-label="checadores table">
            <TableHead style={{ borderRadius: 0 }}>
              <TableRow>
                <TableCell align="center" variant="head" padding="none" colSpan="1">
                  Nombre
                </TableCell>
                <TableCell align="center" variant="head" padding="none" colSpan="1">
                  Teléfono
                </TableCell>
                <TableCell align="center" variant="head" padding="none" colSpan="1">
                  Fecha de ingreso
                </TableCell>
                <TableCell align="center" variant="head" padding="none" colSpan="1">
                  Hora de ingreso
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogins.map((row) => (
                <TableRow key={`login-${row.id}`} sx={{ '& > *': { borderBottom: 'unset' } }}>
                  <TableCell variant="body" padding="none" component="th" scope="row" align="center">{row.nombre}</TableCell>
                  <TableCell variant="body" padding="none" component="th" scope="row" align="center">{row.telefono}</TableCell>
                  <TableCell variant="body" padding="none" component="th" scope="row" align="center">{formatFecha(row.fechaLogin)}</TableCell>
                  <TableCell variant="body" padding="none" component="th" scope="row" align="center">{formatHora(row.fechaLogin)}</TableCell>
                </TableRow>
              ))}
              {filteredLogins.length === 0 && (
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                  <TableCell variant="body" padding="none" component="th" scope="row" align="center" colSpan="4">
                    No hay registros.
                  </TableCell>
                </TableRow>
              )}
              {Array.from({ length: ghostRows }).map((_, i) => {
                const opacity = 1 - (i + 1) / (ghostRows + 1);
                return (
                  <TableRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`ghost-${i}`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    <TableCell
                      colSpan={4}
                      padding="none"
                      style={{
                        borderBottom: `1px solid rgba(224, 224, 224, ${opacity})`,
                      }}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </TableContainer>
        <div style={footerStyle}>
          {`Total: ${filteredLogins.length}`}
        </div>
      </Paper>
    </div>
  );
};

export default CheckerLoginsScreen;
